import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, Loader2, FileText, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import Tesseract from 'tesseract.js';
import { supabase } from '@/integrations/supabase/client';
// A repair order is a customer document. Diagnostics about PARSING it are
// useful; its CONTENTS do not belong in a browser console. Error strings go
// through the project's existing redactor rather than a second copy of the
// same regexes.
import { redactPII } from '@/utils/scrubBugContext';

interface ROUploadProps {
  /** The active shop: parse-ro leaves it a VIN decode receipt (no second NHTSA request). */
  shopId?: string | null;
  onDataExtracted: (data: {
    ticketNumber?: string;
    year?: number;
    make?: string;
    model?: string;
    engineSize?: string;
    vin?: string;
    licensePlate?: string;
    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;
    mileage?: number | string;
    // AII-5: optional per-field confidence ('high'|'medium'|'low'|'none').
    field_confidence?: Record<string, 'high' | 'medium' | 'low' | 'none'>;
    // ShopMonkey review context. These travel as their OWN fields so they are
    // never mistaken for work: complaints and unsold recommendations are shown
    // to the advisor and are deliberately NOT turned into jobs.
    source_format?: string;
    customer_comments?: string[];
    recommendations?: Array<{ description?: string; price?: number }>;
    service_writer?: string;
    created_date?: string;
    /** Sold Labor rows that matched no catalog entry — advisory, not jobs. */
    unmatched_jobs?: Array<{ jobName?: string; hours?: number; notes?: string }>;
    jobs?: Array<{
      category: string;
      submenu: string;
      name: string;
      hours: number;
      notes?: string;
      bay: string;
      skill: number;
    }>;
  }) => void;
}

/**
 * A loggable, redacted one-liner for an unknown thrown/returned value.
 *
 * `String(err)` on a plain object yields "[object Object]", which is safe but
 * useless — and supabase's `functions.invoke` returns exactly that shape. Read
 * `message` wherever it exists, then redact.
 */
const describeErrorForLog = (err: unknown): string => {
  if (err instanceof Error) return redactPII(err.message);
  if (typeof err === 'string') return redactPII(err);
  const message = (err as { message?: unknown })?.message;
  if (typeof message === 'string') return redactPII(message);
  // Deliberately NOT serialized. A FunctionsError carries the whole response,
  // so `JSON.stringify` here would print the repair order — and redaction only
  // covers VINs, phones and emails, not names or complaint text. An opaque
  // shape is worth less than a message, but it is worth more than a leak.
  const name = (err as { name?: unknown })?.name;
  return typeof name === 'string' ? `${name} (no message)` : 'unknown error (no message)';
};

export const ROUpload = ({ onDataExtracted, shopId }: ROUploadProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const MAX_FILE_SIZE_MB = 10;
  const MAX_PDF_PAGES = 5;

  const validateAndProcessFile = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (JPG, PNG, etc.) or PDF',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast({
        title: 'File too large',
        description: `Maximum file size is ${MAX_FILE_SIZE_MB}MB. Your file is ${fileSizeMB.toFixed(2)}MB.`,
        variant: 'destructive',
      });
      return;
    }

    setUploadedFile(file);
    
    // For images, create preview URL
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
    
    // Automatically process the file
    await processOCR(file);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await validateAndProcessFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await validateAndProcessFile(file);
  };

  const processOCR = async (file: File) => {
    setIsProcessing(true);
    
    try {
      toast({
        title: 'Processing RO',
        description: 'Extracting text from the document...',
      });

      let textToProcess = '';

      // Handle PDF files - process first 3-5 pages
      if (file.type === 'application/pdf') {
        // Use ESM build and local worker to avoid CDN issues
        const pdfjsLib: any = await import('pdfjs-dist/build/pdf.mjs');
        const worker = await import('pdfjs-dist/build/pdf.worker.mjs?url');
        pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default;
        
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        const pageCount = Math.min(pdf.numPages, MAX_PDF_PAGES);
        setTotalPages(pageCount);
        const pageTexts: string[] = [];
        
        // Process multiple pages
        for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
          setCurrentPage(pageNum);
          
          toast({
            title: 'Processing PDF',
            description: `Processing page ${pageNum} of ${pageCount}...`,
          });
          
          const page = await pdf.getPage(pageNum);
          const textContent: any = await page.getTextContent();
          let extracted = (textContent?.items || []).map((item: any) => item.str).join(' ');
          
          // Fallback to OCR if it's a scanned PDF (little/no embedded text)
          if (!extracted || extracted.trim().length < 5) {
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: ctx as any, viewport }).promise;
            const blob: Blob = await new Promise((resolve, reject) =>
              canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))), 'image/png')
            );
            const imageFile = new File([blob], `page-${pageNum}.png`, { type: 'image/png' });
            const { data: { text } } = await Tesseract.recognize(imageFile, 'eng', {
              logger: (m) => {
                if (m.status === 'recognizing text') {
                  console.log(`OCR Progress (Page ${pageNum}): ${Math.round(m.progress * 100)}%`);
                }
              },
            });
            extracted = text;
          }
          
          pageTexts.push(extracted);
        }
        
        // Concatenate all pages
        textToProcess = pageTexts.join('\n\n--- PAGE BREAK ---\n\n');
        // Metadata only: the extracted text is the customer's repair order.
        console.log(
          `[ROUpload] extracted text from ${pageCount} page(s), ${textToProcess.length} chars`,
        );
        setCurrentPage(0);
        setTotalPages(0);
      } else {
        const { data: { text } } = await Tesseract.recognize(file, 'eng', {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
            }
          },
        });
        textToProcess = text;
        console.log(`[ROUpload] OCR extracted ${textToProcess.length} chars`);
      }
      
      // Send the extracted text to the backend for AI-powered parsing
      const { data: parsedData, error: parseError } = await supabase.functions.invoke('parse-ro', {
        // shop_id: the server leaves this shop a VIN decode receipt, so the VIN it
        // decoded is not sent to NHTSA again when the vehicle is decoded/saved.
        body: { text: textToProcess, shop_id: shopId ?? null }
      });

      if (parseError) {
        // The FunctionsError carries the whole response, which can echo the
        // document back. Log the message, redacted, and nothing else.
        console.error('[ROUpload] parse-ro failed:', describeErrorForLog(parseError));
        // 413 = the server's 128 KiB text limit: say so (status only; the body is never read).
        const tooLarge = (parseError as { context?: { status?: number } }).context?.status === 413;
        toast({
          title: tooLarge ? 'Document too large' : 'Processing failed',
          description: tooLarge
            ? 'Repair order text is too large to process. Maximum size is 128 KiB. Split the document or fill in manually.'
            : 'Could not parse the document. Please fill in manually.',
          variant: 'destructive',
        });
        return;
      }
      
      if (parsedData && Object.keys(parsedData).length > 0) {
        onDataExtracted(parsedData);
        const jobCount = parsedData.jobs?.length || 0;
        // Count only the vehicle/customer fields the advisor will see on the
        // form. Jobs, confidence and the ShopMonkey review-context fields are
        // reported separately — counting them here would overstate what was
        // actually prefilled.
        const NON_FIELD_KEYS = new Set([
          'jobs',
          'field_confidence',
          'source_format',
          'customer_comments',
          'recommendations',
          'service_writer',
          'created_date',
          'unmatched_jobs',
        ]);
        const fieldCount = Object.keys(parsedData).filter((k) => !NON_FIELD_KEYS.has(k)).length;
        // AII-5: surface low-confidence parsed fields so the advisor reviews
        // them deliberately — weak fields must not look authoritative.
        const fc = parsedData.field_confidence as
          | Record<string, 'high' | 'medium' | 'low' | 'none'>
          | undefined;
        const lowConfidenceFields = fc
          ? Object.entries(fc).filter(([, v]) => v === 'low').map(([k]) => k)
          : [];
        const lowNote =
          lowConfidenceFields.length > 0
            ? ` ${lowConfidenceFields.length} field(s) have LOW confidence (${lowConfidenceFields.join(', ')}) — verify before submitting.`
            : '';
        toast({
          title: 'RO processed successfully',
          description: `Extracted ${fieldCount} vehicle/customer fields${jobCount > 0 ? ` and ${jobCount} job(s)` : ''}. Please review and adjust as needed.${lowNote}`,
          variant: lowConfidenceFields.length > 0 ? 'destructive' : undefined,
        });
      } else {
        toast({
          title: 'Limited data extracted',
          description: 'Could not find much vehicle or customer information. Please fill in manually.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      // A pdf.js / Tesseract failure can quote the text or a signed URL it
      // was reading. Message only, redacted.
      console.error('[ROUpload] document processing failed:', describeErrorForLog(error));
      toast({
        title: 'Processing failed',
        description: 'Could not extract text from the document. Please try again or fill in manually.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };


  const clearUpload = () => {
    setUploadedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Upload Repair Order (RO)
        </CardTitle>
        <CardDescription>
          Upload an image or PDF of the repair order to automatically extract vehicle and customer information
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!uploadedFile ? (
            <div 
              data-testid="ro-upload-dropzone"
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging 
                  ? 'border-primary bg-primary/5' 
                  : 'hover:border-primary/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <label htmlFor="ro-upload" className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm font-medium mb-1">
                  {isDragging ? 'Drop file here' : 'Click or drag to upload RO'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports JPG, PNG, PDF (max {MAX_FILE_SIZE_MB}MB, {MAX_PDF_PAGES} pages)
                </p>
                <Input
                  id="ro-upload"
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isProcessing}
                  data-testid="ro-file-input"
                />
              </label>
            </div>
          ) : (
            <div className="space-y-3" data-testid="ro-preview-container">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{uploadedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearUpload}
                  disabled={isProcessing}
                  data-testid="ro-clear-button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {previewUrl && (
                <div className="rounded-lg overflow-hidden border">
                  <img 
                    src={previewUrl} 
                    alt="RO Preview" 
                    className="w-full max-h-64 object-contain bg-muted"
                  />
                </div>
              )}
              
              {isProcessing && (
                <div 
                  className="flex items-center justify-center gap-2 p-4 bg-muted rounded-lg"
                  data-testid="ro-processing-indicator"
                >
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p className="text-sm text-muted-foreground">
                    {currentPage > 0 && totalPages > 0
                      ? `Processing page ${currentPage} of ${totalPages}...`
                      : 'Processing document...'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
